import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import apiClient from '@/api/client';
import type { DropdownLookupMap } from '@/types/dropdown-lookup';

export interface DropdownLookupOptions {
  /** Extra query params forwarded to the endpoint (e.g. { vaultTypeId: '1' }) */
  params?: Record<string, string | number | boolean | null | undefined>;
}

/**
 * Fetch and cache dropdown lookup data for the given sources.
 *
 * @param sources - Lookup source names (e.g., ['vaultTypes', 'depositCategories'])
 * @param options - Optional extra query params forwarded to the endpoint
 */
export function useDropdownLookup(
  sources: string[],
  options?: DropdownLookupOptions,
): UseQueryResult<DropdownLookupMap> {
  const sorted = [...sources].sort((a, b) => a.localeCompare(b));
  const extraParams = options?.params ?? {};
  return useQuery({
    queryKey: ['dropdown-lookups', sorted, extraParams],
    queryFn: async () => {
      const response = await apiClient.get<DropdownLookupMap>(
        '/api/dropdown-lookups',
        { params: { sources: sorted.join(','), ...extraParams } },
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: sources.length > 0,
  });
}
